import { Type } from '@angular/core';
import { Observable } from 'rxjs';
import * as CSS from 'csstype';

export interface Icon {
  cls: string;
  css?: CSS.Properties;
}
export enum TableStyle {
  DEFAULT = 'default',
  MODAL = 'modal',
  MODAL_FLUID = 'modalFluid',

  LEGACY_DEFAULT = 'legacyDefault',
  LEGACY_FRAMELESS = 'legacyFrameless',
  LEGACY_HYBRID_CORE = 'legacyHybridCore',
  LEGACY_BORDERED_ROWS = 'legacyBorderedRows'
}
export enum ColumnType {
  DEFAULT = 'default',
  COMPONENT = 'component',
  CURRENCY = 'currency',
  DATE = 'date',
  ENUM = 'enum',
  LINK = 'link',
  NUMERIC = 'numeric',
  SETTINGS = 'settings'
}

export enum TextTransform {
  UPPERCASE = 'uppercase',
  LOWERCASE = 'lowercase',
  CAPITALIZE = 'capitalize'
}

export enum TextAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right'
}

export enum ColumnSortDirection {
  ASC = 'ASC',
  DESC = 'DESC'
}

export interface UserColumn {
  columnId: string;
  // label?: string;
  // type?: ColumnType;
  visible: boolean;
  position: number;
}

export interface InternalColumn extends Column {
  visible: boolean;
  position: number;
}

export interface ColumnSortEvent {
  columnId: string;
  direction: ColumnSortDirection | string;
}

export interface TableStyleConfig {
  rowGap?: number;
  rowHeight?: number;
  rowBorder?: boolean;
  lineHeight?: number;
  cellPaddingX?: number;
  cellPaddingY?: number;
  cellBackground?: string;
  backgroundColour?: string;
  hoverShadow?: boolean;

  frameless?: boolean;
  whiteHeader?: boolean;
  surroundingBorder?: boolean;

  tableHeight?: string;
  fluidTableHeight?: boolean;
}


export interface Column<T extends Type<unknown> = any> {
  /**
   * must be a unique string for each column. Will throw in error if `id` is ambigous somewhere
   */
  id: string;
  /**
   * must be a unique string for each column. Will throw in error if `id` is ambigous somewhere
   */
  headline: string;
  /**
   * Describes an icon of an iconfont (must be loaded) that is displayed in the column headline. `cls` is the icon class itself, optional `css` property can hold an object with additional CSS rules for this icon
   */
  headlineImgClass?: Icon;
  /**
   * If filled with a string will display a question-mark icon next to the headline, with the tooltip text displayed on hover
   */
  headlineTooltip?: string;
  /**
   * Describes an icon of an iconfont (must be loaded) that is displayed before the actual column content on each row
   */
  iconCls?: Icon;
  /**
   * Sets the type of the column, Defaults to `ColumnType.DEFAULT` (simple text column)
   */
  type?: ColumnType;
  /**
   * The key of the field that holds the value for that column in the `data` property of ig-table.
   */
  dataSrc?: string;
  /**
   * A default value if the key in `dataSrc` is undefined. Be sure you don't want `emptyValue`!
   */
  dataDefault?: any;
  /**
   * A value that is displayed if the value of `dataSrc` is empty (Note the difference to `dataDefault`! Here we also use that value on empty strings). Does not apply to `ColumnType.COMPONENT`
   */
  emptyValue?: string;
  /**
   * Applies a CSS text-transform on the cells of that columns (except the header)
   */
  textTransform?: TextTransform;
  /**
   * Add CSS class for text alignement to all cells of that column (including header cell)
   */
  textAlign?: TextAlign;
  /**
   * This is not used anymore, only here for historical reasons
   * @deprecated
   */
  additionalDataSources?: string[];
  /**
   * If `type` is `ColumnType.ENUM` it holds the mapping value->label
   */
  enum?: Record<string, string>;
  /**
   * The class of an angular component if `type` is `ColumnType.COMPONENT``
   */
  component?: T;
  /**
   * Additional input properties provided to the angular component in `component`
   */
  componentProperties?: InstanceType<T>;
  /**
   * Define if this column is sortable (defaults to false)
   */
  sortable?: boolean;
  /**
   * Defines a min width in pixels for that column (if not provided the width is 1fr)
   */
  width?: number;
  /**
   * Make the width above fixed
   */
  fixedWidth?: boolean;
  /**
   * Defines if the column is visible by default if there is no `userColumns` configuration. Defaults to false. Remember to set to true if you don't provide `userColumns`
   */
  preselected?: boolean;
  /**
   * A string to display before the actual row content in each row (only for `ColumnType.DATE` and `ColumnType.DEFAULT`)
   */
  prefix?: string;
  /**
   * A string to display after the actual row content in each row (only for `ColumnType.DATE` and `ColumnType.DEFAULT`)
   */
  suffix?: string;
  /**
   * A [date format](https://angular.io/api/common/DatePipe#pre-defined-format-options) if `type` is `ColumnType.DATE`.
   */
  dateFormat?: string;
  /**
   * An Observable resolving with a currency symbol. Applies only if `type` is `ColumnType.CURRENCY``
   */
  currencySymbol?: Observable<string>;
  /**
   * [digitsInfo](https://angular.io/api/common/CurrencyPipe) for currencyPipe. Applies only if `type` is `ColumnType.CURRENCY``
   */
  currencyDigitsInfo?: string;
  /**
   * colour coding for positive and negative amounts. Applies only if `type` is `ColumnType.CURRENCY``
   */
  currencyColourCoding?: boolean;

  truncate?: boolean;
}

export interface SelectedAction {
  /**
   * function which returns if the current action item should be hidden (true=hide, false=show). If this function is not present, item is always shown
   */
  hideFunc?: () => boolean;
  /**
   * function which is executed on action click
   */
  func: () => void;
  /**
   * if the action should be shown as disabled
   */
  disabled?: boolean;
  /**
   * icon class (icon font)
   */
  icon?: string;
  /**
   * label to display
   */
  label: string;
}
